import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { fetchInternalOrderById } from '@/api/orders';
import { InternalOrderDetailHeader } from '@/app/(client)/orders/-components/InternalOrderDetailHeader';
import { OrderDetailError } from '@/app/(client)/orders/-components/OrderDetailError';
import { SalesOrderDetailHeaderSkeleton } from '@/app/(client)/orders/-components/SalesOrderDetailHeaderSkeleton';
import {
  internalOrderDetailKeys,
  useInternalOrderDetail,
} from '@/app/(client)/orders/-hooks/useInternalOrderDetail';
import type { TabEntry } from '@/components/shared/PageTabs';
import PageTabs from '@/components/shared/PageTabs';
import { queryClient } from '@/lib/utils/queryClient';
import { useOrdersTranslator } from '../../../../-hooks/useOrdersTranslator';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/(detail)/internal-orders/$ioId',
)({
  beforeLoad: ({ params, location }) => {
    const bare = `/orders/internal-orders/${params.ioId}`;
    if (location.pathname === bare || location.pathname === `${bare}/`) {
      throw redirect({
        to: '/orders/internal-orders/$ioId/overview',
        params: { ioId: params.ioId },
      });
    }
  },
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: internalOrderDetailKeys.detail(params.ioId),
      queryFn: () => fetchInternalOrderById(params.ioId).then(res => res.data),
    }),
  component: InternalOrderDetailLayout,
  errorComponent: OrderDetailError,
});

function InternalOrderDetailLayout() {
  const { ioId } = Route.useParams();
  const { getTranslation: t } = useOrdersTranslator();
  const { data: order } = useInternalOrderDetail(ioId);

  const base = `/orders/internal-orders/${ioId}`;
  const tabs: TabEntry[] = [
    { key: 'overview', label: t('detail.tabOverview'), to: `${base}/overview` },
    { key: 'tracing', label: t('detail.tabTracing'), to: `${base}/tracing` },
    { key: 'products', label: t('detail.tabProducts'), to: `${base}/products` },
    {
      key: 'documents',
      label: t('detail.tabDocuments'),
      to: `${base}/documents`,
    },
  ];

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      {order ? (
        <InternalOrderDetailHeader order={order} />
      ) : (
        <SalesOrderDetailHeaderSkeleton />
      )}
      <div className='sticky top-0 z-20 bg-background'>
        <PageTabs tabs={tabs} mode='path' moreLabel={t('detail.moreTabs')} />
      </div>
      <div className='@container flex flex-col flex-1 min-h-[70dvh] p-4 sm:p-6 w-full'>
        <Outlet />
      </div>
    </div>
  );
}
